import { createMediaSwitch, rebindWithHandleMediaUpdated } from "./mediaSwitch.js"
import { wireInsertions } from "./trackContract.js"

const noPlayback = { stopLoad() {}, startLoad() {} }

/**
 * The in-player audio⇄video toggle (SC-1323 §4.2) over one <media-controller>.
 *
 * sameTimeline: the two tracks are renditions of one item (a podcast episode whose audio was
 * taken from its video). The position carries over, mapped through the audio track's DC
 * insertions: today's podcast switch, unchanged.
 *
 * Separate tracks (a course EPISODE ⇄ VIDEO item): the target resumes at its own last position
 * in this page, else at resumeFor(trackId). The position is never carried over.
 *
 * switchTo() runs synchronously up to and including play(), so calling it from a click handler
 * keeps the user gesture iOS needs. onSwitchPause(el) runs before the left element is paused,
 * only when it is playing, so the pause event can be flagged as the switch's.
 */
export function createTrackSwitcher({
    controller,
    video,
    audio,
    tracks,
    sameTimeline,
    initialMode = "video",
    playback = noPlayback,
    resumeFor = () => 0,
    onSwitchPause = () => {},
    rebind = rebindWithHandleMediaUpdated,
}) {
    const videoTrack = tracks.find((track) => track.kind === "video") ?? null
    const audioTrack = tracks.find((track) => track.kind === "audio") ?? null

    if (!videoTrack || !audioTrack || !video || !audio) {
        return null
    }

    const trackFor = (mode) => (mode === "audio" ? audioTrack : videoTrack)
    const elementFor = (track) => (track.kind === "audio" ? audio : video)
    const positions = new Map()
    let active = trackFor(initialMode)

    const mapPosition = sameTimeline
        ? null
        : (next, position) => {
              positions.set(active.id, position)
              const target = trackFor(next)

              return positions.get(target.id) ?? resumeFor(target.id)
          }

    const mediaSwitch = createMediaSwitch({
        controller,
        video,
        audio,
        audioUrl: audioTrack.media.audio.url,
        insertions: wireInsertions(audioTrack),
        playback,
        initialMode,
        mapPosition,
        rebind,
    })

    return {
        switchTo(next, { play = true } = {}) {
            if (next === active.kind || (next !== "audio" && next !== "video")) {
                return null
            }

            const fromTrack = active
            const fromEl = elementFor(fromTrack)

            if (!fromEl.paused) {
                onSwitchPause(fromEl)
            }

            const playing = mediaSwitch.switchTo(next, { play })

            if (playing === null) {
                return null
            }

            active = trackFor(next)

            return { playing, fromTrack, toTrack: active }
        },
        get activeTrack() {
            return active
        },
    }
}
